
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function AntivirusSecurityPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    securityNeeds: [] as string[],
    contactMethod: '',
    message: '',
  });
  const [messageLength, setMessageLength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'message') {
      setMessageLength(value.length);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      securityNeeds: checked
        ? [...prev.securityNeeds, value]
        : prev.securityNeeds.filter(need => need !== value)
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, contactMethod: e.target.value }));
  };

  const formUrl = isNL
    ? 'https://readdy.ai/api/form/d6dgh0uqd308q1a0a1rg'
    : 'https://readdy.ai/api/form/d50s13g9ei8qjpb9fn8h';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('employees', formData.employees);
      formBody.append('securityNeeds', formData.securityNeeds.join(', '));
      formBody.append('contactMethod', formData.contactMethod);
      formBody.append('message', formData.message);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          employees: '',
          securityNeeds: [],
          contactMethod: '',
          message: '',
        });
        setMessageLength(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Security needs options
  const securityNeedsOptions = [
    { value: 'Endpoint Protection', label: isNL ? 'Eindpuntbeveiliging' : 'Endpoint Protection' },
    { value: 'Server Security', label: isNL ? 'Serverbeveiliging' : 'Server Security' },
    { value: 'Mobile Security', label: isNL ? 'Mobiele Beveiliging' : 'Mobile Security' },
    { value: 'Email Security', label: isNL ? 'E-mailbeveiliging' : 'Email Security' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative w-full h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/9b654071844d9556f1e4c9358797742e.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-800/75 to-transparent"></div>
        <div className="container mx-auto px-4 h-full flex items-center relative">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {isNL ? 'Zakelijke Antivirus- & Beveiligingsoplossingen' : 'Enterprise Antivirus & Security Solutions'}
            </h1>
            <p className="text-lg text-gray-100 mb-8">
              {isNL
                ? 'Bescherm uw bedrijfsendpoints met geavanceerde antivirus- en beveiligingsoplossingen die verdedigen tegen malware, ransomware en opkomende dreigingen.'
                : 'Protect your business endpoints with advanced antivirus and security solutions that defend against malware, ransomware, and emerging threats.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                {isNL ? 'Word Vandaag Beschermd' : 'Get Protected Today'}
              </button>
              <button className="bg-white text-gray-800 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                {isNL ? 'Bekijk Oplossingen' : 'View Solutions'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Protection Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Uitgebreide Beveiligingsdiensten' : 'Comprehensive Protection Services'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Onze zakelijke antivirusoplossingen bieden meerlaagse bescherming voor uw endpoints, servers en mobiele apparaten.'
                : 'Our enterprise antivirus solutions provide multi-layered protection for your endpoints, servers, and mobile devices.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-shield-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Eindpuntbeveiliging' : 'Endpoint Protection'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Geavanceerde eindpuntbeveiliging met realtime dreigingsdetectie, gedragsanalyse en geautomatiseerde responsmogelijkheden.'
                  : 'Advanced endpoint security with real-time threat detection, behavioral analysis, and automated response capabilities.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Realtime Malwaredetectie' : 'Real-time Malware Detection'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Ransomwarebescherming' : 'Ransomware Protection'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Gedragsanalyse' : 'Behavioral Analysis'}</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-server-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Serverbeveiliging' : 'Server Security'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Bescherm uw kritieke servers met zakelijke antivirusoplossingen ontworpen voor hoogwaardige omgevingen.'
                  : 'Protect your critical servers with enterprise-grade antivirus solutions designed for high-performance environments.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Windows & Linux Serverbescherming' : 'Windows & Linux Server Protection'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Lage Systeembelasting' : 'Low System Impact'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Centraal Beheer' : 'Centralized Management'}</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-smartphone-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Mobiele Apparaatbeveiliging' : 'Mobile Device Security'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Beveilig uw mobiele medewerkers met uitgebreide bescherming voor iOS- en Android-apparaten.'
                  : 'Secure your mobile workforce with comprehensive protection for iOS and Android devices.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Mobiele Dreigingsverdediging' : 'Mobile Threat Defense'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'App-beveiligingsscanning' : 'App Security Scanning'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">{isNL ? 'Apparaatbeheer' : 'Device Management'}</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Antivirus Products */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Zakelijke Antivirusproducten' : 'Enterprise Antivirus Products'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Toonaangevende antivirus- en eindpuntbeveiligingsoplossingen van vertrouwde beveiligingsleveranciers.'
                : 'Industry-leading antivirus and endpoint protection solutions from trusted security vendors.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Product 1 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Symantec%20Endpoint%20Protection%20enterprise%20antivirus%20software%20box%20and%20interface%20showing%20security%20dashboard%20with%20yellow%20and%20black%20branding.%20Professional%20enterprise%20security%20software%20packaging%20on%20clean%20white%20background%20with%20security%20shield%20logo.&width=400&height=300&seq=symantec1&orientation=landscape"
                  alt="Symantec Endpoint Protection"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Symantec Endpoint Protection</h3>
                <p className="text-gray-600 mb-4">
                  {isNL
                    ? 'Uitgebreide eindpuntbeveiliging met geavanceerde dreigingsbescherming en machine learning-mogelijkheden.'
                    : 'Comprehensive endpoint security with advanced threat protection and machine learning capabilities.'}
                </p>
                <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm w-full hover:bg-primary/90 transition-colors cursor-pointer">
                  {isNL ? 'Offerte Aanvragen' : 'Request Quote'}
                </button>
              </div>
            </div>

            {/* Product 2 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=McAfee%20Enterprise%20Security%20software%20interface%20showing%20antivirus%20protection%20dashboard%20with%20red%20branding%20and%20security%20status.%20Professional%20enterprise%20antivirus%20software%20on%20clean%20white%20background%20with%20shield%20protection%20icon.&width=400&height=300&seq=mcafee1&orientation=landscape"
                  alt="McAfee Enterprise Security"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">McAfee Enterprise Security</h3>
                <p className="text-gray-600 mb-4">
                  {isNL
                    ? 'Geïntegreerde eindpuntbescherming met cloud-native architectuur en AI-gestuurde dreigingsdetectie.'
                    : 'Unified endpoint protection with cloud-native architecture and AI-powered threat detection.'}
                </p>
                <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm w-full hover:bg-primary/90 transition-colors cursor-pointer">
                  {isNL ? 'Offerte Aanvragen' : 'Request Quote'}
                </button>
              </div>
            </div>

            {/* Product 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Kaspersky%20Endpoint%20Security%20enterprise%20antivirus%20software%20showing%20security%20interface%20with%20green%20and%20red%20branding.%20Professional%20enterprise%20security%20software%20packaging%20on%20clean%20white%20background%20with%20protection%20shield.&width=400&height=300&seq=kaspersky1&orientation=landscape"
                  alt="Kaspersky Endpoint Security"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Kaspersky Endpoint Security</h3>
                <p className="text-gray-600 mb-4">
                  {isNL
                    ? 'Meerlaagse bescherming met exploitpreventie, ransomwarebescherming en webcontrole.'
                    : 'Multi-layered protection with exploit prevention, ransomware protection, and web control.'}
                </p>
                <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm w-full hover:bg-primary/90 transition-colors cursor-pointer">
                  {isNL ? 'Offerte Aanvragen' : 'Request Quote'}
                </button>
              </div>
            </div>

            {/* Product 4 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Trend%20Micro%20Apex%20One%20enterprise%20endpoint%20security%20software%20interface%20with%20blue%20and%20red%20branding%20showing%20security%20dashboard.%20Professional%20enterprise%20antivirus%20solution%20on%20clean%20white%20background.&width=400&height=300&seq=trendmicro1&orientation=landscape"
                  alt="Trend Micro Apex One"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Trend Micro Apex One</h3>
                <p className="text-gray-600 mb-4">
                  {isNL
                    ? 'Geavanceerde geautomatiseerde dreigingsdetectie en -respons met XDR-mogelijkheden voor endpoints.'
                    : 'Advanced automated threat detection and response with XDR capabilities for endpoints.'}
                </p>
                <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm w-full hover:bg-primary/90 transition-colors cursor-pointer">
                  {isNL ? 'Offerte Aanvragen' : 'Request Quote'}
                </button>
              </div>
            </div>
          </div>
          <div className="text-center mt-10">
            <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
              {isNL ? 'Bekijk Alle Antivirusoplossingen' : 'View All Antivirus Solutions'}
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Our Antivirus Solutions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">
                {isNL ? 'ZAKELIJKE BESCHERMING' : 'ENTERPRISE PROTECTION'}
              </span>
              <h2 className="text-3xl font-bold mb-6">
                {isNL ? 'Waarom Kiezen voor Onze Antivirusoplossingen' : 'Why Choose Our Antivirus Solutions'}
              </h2>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Onze zakelijke antivirusoplossingen bieden uitgebreide bescherming tegen de nieuwste dreigingen, terwijl de systeemprestaties en gebruikersproductiviteit behouden blijven.'
                  : 'Our enterprise antivirus solutions provide comprehensive protection against the latest threats while maintaining system performance and user productivity.'}
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-green-600">
                      <i className="ri-shield-star-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {isNL ? 'Geavanceerde Dreigingsdetectie' : 'Advanced Threat Detection'}
                    </h3>
                    <p className="text-gray-600">
                      {isNL
                        ? 'AI-gestuurde detectie-engines identificeren en blokkeren bekende en onbekende dreigingen in realtime.'
                        : 'AI-powered detection engines identify and block known and unknown threats in real-time.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-green-600">
                      <i className="ri-dashboard-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {isNL ? 'Centraal Beheer' : 'Centralized Management'}
                    </h3>
                    <p className="text-gray-600">
                      {isNL
                        ? 'Eén console om de beveiliging van alle endpoints, servers en mobiele apparaten te beheren.'
                        : 'Single console to manage security across all endpoints, servers, and mobile devices.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-green-600">
                      <i className="ri-speed-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {isNL ? 'Minimale Prestatie-impact' : 'Minimal Performance Impact'}
                    </h3>
                    <p className="text-gray-600">
                      {isNL
                        ? 'Geoptimaliseerde scan-engines zorgen voor bescherming zonder uw systemen te vertragen.'
                        : 'Optimized scanning engines ensure protection without slowing down your systems.'}
                    </p>
                  </div>
                </div>
              </div>
              <button className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                {isNL ? 'Begin Vandaag' : 'Get Started Today'}
              </button>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Enterprise%20antivirus%20security%20operations%20center%20with%20IT%20professionals%20monitoring%20endpoint%20protection%20on%20multiple%20screens%20showing%20threat%20detection%20dashboards.%20Modern%20security%20monitoring%20environment%20with%20green%20security%20indicators%20and%20real-time%20threat%20analysis%20displays.%20Professional%20cybersecurity%20team%20working%20in%20high-tech%20operations%20center.&width=800&height=600&seq=antivirusops1&orientation=landscape"
                  alt={isNL ? 'Antivirus Beveiligingsoperaties' : 'Antivirus Security Operations'}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Protection Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Uitgebreide Beveiligingsfuncties' : 'Comprehensive Protection Features'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Onze antivirusoplossingen bevatten een compleet pakket beveiligingsfuncties om uw bedrijf vanuit alle hoeken te beschermen.'
                : 'Our antivirus solutions include a complete suite of security features to protect your business from all angles.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-virus-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'Anti-malwarebescherming' : 'Anti-Malware Protection'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Detecteer en verwijder virussen, trojans, wormen en andere kwaadaardige software voordat ze schade kunnen aanrichten.'
                  : 'Detect and remove viruses, trojans, worms, and other malicious software before they can cause damage.'}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-lock-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'Ransomwareverdediging' : 'Ransomware Defense'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Geavanceerde gedragsanalyse en bestandsbescherming om ransomware-versleutelingsaanvallen te voorkomen.'
                  : 'Advanced behavioral analysis and file protection to prevent ransomware encryption attacks.'}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-global-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'Webbescherming' : 'Web Protection'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Blokkeer kwaadaardige websites, phishingpogingen en drive-by downloads om gebruikers online veilig te houden.'
                  : 'Block malicious websites, phishing attempts, and drive-by downloads to keep users safe online.'}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-mail-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'E-mailbeveiliging' : 'Email Security'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Scan e-mailbijlagen en links om malwareverspreiding via e-mailkanalen te voorkomen.'
                  : 'Scan email attachments and links to prevent malware delivery through email channels.'}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-firewall-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'Firewall-integratie' : 'Firewall Integration'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Ingebouwde firewallbescherming om netwerkverkeer te controleren en ongeautoriseerde toegang te voorkomen.'
                  : 'Built-in firewall protection to control network traffic and prevent unauthorized access.'}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-usb-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isNL ? 'Apparaatbeheer' : 'Device Control'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Beheer en controleer USB-apparaten en verwisselbare media om gegevensdiefstal en malwareverspreiding te voorkomen.'
                  : 'Manage and control USB devices and removable media to prevent data theft and malware spread.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request Protection Quote Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left Column - Info */}
              <div className="w-full md:w-5/12 bg-primary p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">
                  {isNL ? 'Beveiligingsofferte Aanvragen' : 'Request Protection Quote'}
                </h2>
                <p className="mb-6">
                  {isNL
                    ? 'Ontvang een offerte op maat voor zakelijke antivirus- en eindpuntbeveiligingsoplossingen afgestemd op uw bedrijfsbehoeften.'
                    : 'Get a customized quote for enterprise antivirus and endpoint protection solutions tailored to your business needs.'}
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-shield-check-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {isNL ? 'Zakelijke Bescherming' : 'Enterprise-Grade Protection'}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {isNL
                          ? 'Toonaangevende antivirusoplossingen van vertrouwde beveiligingsleveranciers.'
                          : 'Industry-leading antivirus solutions from trusted security vendors.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-price-tag-3-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {isNL ? 'Concurrerende Prijzen' : 'Competitive Pricing'}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {isNL
                          ? 'Volumelicenties en flexibele betalingsopties voor bedrijven van elke omvang.'
                          : 'Volume licensing and flexible payment options for businesses of all sizes.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-customer-service-2-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {isNL ? 'Deskundige Ondersteuning' : 'Expert Support'}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {isNL
                          ? 'Professionele installatie, configuratie en doorlopende ondersteuningsdiensten.'
                          : 'Professional installation, configuration, and ongoing support services.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-6">
                  <p className="font-semibold mb-2">
                    {isNL ? 'Neem contact op met ons beveiligingsteam:' : 'Contact our security team:'}
                  </p>
                  <div className="flex items-center mb-2">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-phone-line"></i>
                    </div>
                    <span>+31 (0)616300011</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-mail-line"></i>
                    </div>
                    <span>info@thyamis.com</span>
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="w-full md:w-7/12 p-8">
                <form id={isNL ? 'antivirus-quote-form-nl' : 'antivirus-quote-form'} data-readdy-form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'Voornaam' : 'First Name'}
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder={isNL ? 'Voer voornaam in' : 'Enter first name'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'Achternaam' : 'Last Name'}
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder={isNL ? 'Voer achternaam in' : 'Enter last name'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'E-mailadres' : 'Email Address'}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder={isNL ? 'Voer e-mailadres in' : 'Enter email address'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'Telefoonnummer' : 'Phone Number'}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder={isNL ? 'Voer telefoonnummer in' : 'Enter phone number'}
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'Bedrijfsnaam' : 'Company Name'}
                      </label>
                      <input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder={isNL ? 'Voer bedrijfsnaam in' : 'Enter company name'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="employees" className="block text-sm font-medium text-gray-700 mb-1">
                        {isNL ? 'Aantal Medewerkers' : 'Number of Employees'}
                      </label>
                      <div className="relative">
                        <select
                          id="employees"
                          name="employees"
                          value={formData.employees}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors appearance-none pr-8"
                          required
                        >
                          <option value="" disabled>
                            {isNL ? 'Selecteer bereik' : 'Select range'}
                          </option>
                          <option value="1-50">1-50</option>
                          <option value="51-200">51-200</option>
                          <option value="201-500">201-500</option>
                          <option value="501-1000">501-1000</option>
                          <option value="1000+">1000+</option>
                        </select>
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-500">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Beveiligingsbehoeften' : 'Security Needs'}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {securityNeedsOptions.map((option) => (
                        <label key={option.value} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="securityNeeds"
                            value={option.value}
                            checked={formData.securityNeeds.includes(option.value)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          <span className="text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Gewenste Contactmethode' : 'Preferred Contact Method'}
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Email"
                          checked={formData.contactMethod === 'Email'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">E-mail</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Phone"
                          checked={formData.contactMethod === 'Phone'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">{isNL ? 'Telefoon' : 'Phone'}</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      {isNL ? 'Aanvullende Vereisten' : 'Additional Requirements'}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                      placeholder={isNL ? 'Vertel ons over uw specifieke beveiligingsvereisten (max 500 tekens)' : 'Tell us about your specific security requirements (max 500 characters)'}
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      <span>{messageLength}</span>/500 {isNL ? 'tekens' : 'characters'}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium w-full hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting
                      ? (isNL ? 'Verzenden...' : 'Submitting...')
                      : (isNL ? 'Offerte Aanvragen' : 'Request Quote')}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                      {isNL
                        ? 'Bedankt! Uw offerteaanvraag is succesvol verzonden. Ons team neemt binnenkort contact met u op.'
                        : 'Thank you! Your quote request has been submitted successfully. Our team will contact you soon.'}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      {isNL
                        ? 'Sorry, er is een fout opgetreden bij het verzenden van uw aanvraag. Probeer het opnieuw of neem rechtstreeks contact met ons op.'
                        : 'Sorry, there was an error submitting your request. Please try again or contact us directly.'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
